import { cellAt } from '../board/board';
import { newPiece } from './piece';
import { maxSpellPoints } from './spellCosts';
import { LUMINOSITY_STEPS, type LuminosityStart } from '../board/types';
import { createRng } from '../rng/seededRandom';
import { advanceLuminosity, initialLuminosity, isAtFavourablePeak } from './luminosity';
import { destinationsFor, pieceById } from './movement';
import { castSpell, isLegalSpell, spellMoves } from './spells';
import {
  NEUTRAL_SENTENCE,
  type BattleResult,
  type FactionId,
  type GameContext,
  type GameState,
  type LuminosityState,
  type Move,
  type Piece,
  type PieceId,
  type SpellId,
} from './types';

export interface GameOptions {
  seed: number;
  /** Turn order; defaults to the board's faction order. */
  factionOrder?: FactionId[];
  /**
   * How many of the board's factions play, at least two. A board designed for more players than
   * sit down drops its highest-numbered factions: they get no pieces and never take a turn.
   */
  players?: number;
  /**
   * Where the luminosity cycle starts; overrides the board's own `luminosity`. Left out on a
   * board without one, the seed picks a fair start (see `initialLuminosity`).
   */
  luminosityStart?: LuminosityStart;
  /**
   * The factions that play, by id, when they are not simply the first `players` of the board:
   * a setup may leave any side out. At least two; the rest get no pieces and never take a turn.
   */
  playing?: FactionId[];
  /** Muster offers other factions a chance to commit one reachable main before Card or Roll. */
  engagement?: 'immediate' | 'muster';
}

/**
 * A game with no battle and no lasting spell for this many turns is a draw: the original's rule,
 * without the spells that only move or hold pieces for a while (`NO_PROGRESS_SPELLS`).
 */
export const MAX_QUIET_TURNS = 40;

/**
 * Turns left before the quiet-turn draw, counting this one: what the table shows as the count
 * runs out, where the draw used to come without warning (core-rules:quality#13). A fight, Heal,
 * Revive or Summon Elemental starts the count again.
 */
export function quietTurnsLeft(state: GameState): number {
  return Math.max(0, MAX_QUIET_TURNS - (state.turn - state.lastProgressTurn));
}
/**
 * The backstop: however lively a game looks, it is a draw once it has run this long. The
 * quiet-turn rule above catches a game where nothing lasting happens, since holding a piece for a
 * while is no progress (`NO_PROGRESS_SPELLS`); this catches whatever else might still keep a game
 * going to no end. A cap cannot be played around, so there is one.
 *
 * Well clear of a real game: over twenty-four simulated games on the classic board the median ran
 * 186 turns and the longest 467. Anything near this cap has already stopped being a game.
 */
export const MAX_GAME_TURNS = 1000;

/**
 * How many rounds the turn walk may pass over before it gives up on anyone acting again. Every
 * hold ends well inside it: Freeze and a neutral sentence within five of the victim's turns, a
 * Light or Dark prisoner at its side's peak, which the cycle reaches within ten rounds.
 */
const MAX_WALK_ROUNDS = 2 * LUMINOSITY_STEPS + NEUTRAL_SENTENCE;

export class IllegalMoveError extends Error {
  constructor(message: string) {
    super(`Illegal move: ${message}`);
    this.name = 'IllegalMoveError';
  }
}

export function createGame(ctx: GameContext, options: GameOptions): GameState {
  for (const p of ctx.board.def.placements) {
    if (!ctx.roster[p.character])
      throw new Error(`Board places unknown character '${p.character}'`);
    if (ctx.roster[p.character]?.summoned)
      throw new Error(`Board places summoned character '${p.character}'`);
  }
  const order = options.factionOrder ?? ctx.board.def.factions.map((f) => f.id);
  const players = options.players ?? order.length;
  if (players < 2 || players > order.length)
    throw new Error(`${players} players on a board for ${order.length}`);
  const playing = new Set(options.playing ?? order.slice(0, players));
  for (const id of playing)
    if (!order.includes(id)) throw new Error(`Unknown faction '${id}' cannot play`);
  if (playing.size < 2) throw new Error('At least two factions must play');
  const pieces: Piece[] = ctx.board.def.placements
    .filter((p) => playing.has(p.faction))
    .map((p, i) => newPiece(ctx.roster, i + 1, p.faction, p.character, p.x, p.y));
  return {
    turn: 0,
    round: 0,
    lastProgressTurn: 0,
    factions: order.map((id) => ({
      id,
      spellsCast: 0,
      graveyard: [],
      eliminated: !playing.has(id),
    })),
    activeFaction: order[0]!,
    pieces,
    nextPieceId: pieces.length + 1,
    luminosity: initialLuminosity(
      options.seed,
      options.luminosityStart ?? ctx.board.def.luminosity,
    ),
    phase: 'play',
    pendingBattle: null,
    winner: null,
    rng: createRng(options.seed).state(),
    engagement: options.engagement ?? 'immediate',
  };
}

/**
 * Every move the active faction may make. Letting the round pass (Shift Time) is always among them,
 * so a side is never without one; a plain `pass` is never offered (see `timeoutMove`).
 */
export function legalMoves(ctx: GameContext, state: GameState): Move[] {
  if (state.phase === 'muster') {
    const battle = state.pendingBattle;
    const faction = battle?.responders?.[battle.responseIndex ?? 0];
    if (!battle || !faction) return [];
    const joined = new Set(battle.participantIds ?? [battle.attackerId, battle.defenderId]);
    const moves: Move[] = state.pieces
      .filter((piece) => piece.faction === faction && !joined.has(piece.id))
      .filter((piece) =>
        destinationsFor(ctx, { ...state, phase: 'play' }, piece).some(
          (d) => d.x === battle.x && d.y === battle.y,
        ),
      )
      .map((piece) => ({ type: 'join-battle' as const, pieceId: piece.id }));
    return [...moves, { type: 'pass-battle' }];
  }
  if (state.phase !== 'play') return [];
  const moves: Move[] = [];
  for (const piece of state.pieces) {
    if (piece.faction !== state.activeFaction) continue;
    for (const d of destinationsFor(ctx, state, piece))
      moves.push({ type: 'move', pieceId: piece.id, x: d.x, y: d.y });
  }
  moves.push(...spellMoves(ctx, state));
  return moves;
}

/**
 * What the turn clock plays for a side out of time: nothing at all. The turn goes by without a
 * move, and a muster response declines to join. It must not be a good move: the clock used to play
 * the AI's best one, and for most players that was better than their own, so idling paid.
 */
export function timeoutMove(state: GameState): Move {
  return state.phase === 'muster' ? { type: 'pass-battle' } : { type: 'pass' };
}

/**
 * Applies a move. A move onto an enemy leaves the game in the `battle` phase with a pending
 * battle; call `applyBattleResult` (arena or auto-resolve) to continue.
 */
export function applyMove(ctx: GameContext, state: GameState, move: Move): GameState {
  if (state.phase === 'muster') {
    const battle = state.pendingBattle;
    const faction = battle?.responders?.[battle.responseIndex ?? 0];
    if (!battle || !faction) throw new IllegalMoveError('no muster response pending');
    const legal = legalMoves(ctx, state);
    if (
      !legal.some(
        (candidate) =>
          candidate.type === move.type &&
          (candidate.type !== 'join-battle' ||
            (move.type === 'join-battle' && candidate.pieceId === move.pieceId)),
      )
    )
      throw new IllegalMoveError('illegal muster response');
    const piece = move.type === 'join-battle' ? pieceById(state, move.pieceId) : undefined;
    const participantIds = piece
      ? [...(battle.participantIds ?? [battle.attackerId, battle.defenderId]), piece.id]
      : (battle.participantIds ?? [battle.attackerId, battle.defenderId]);
    const responseIndex = (battle.responseIndex ?? 0) + 1;
    const done = responseIndex >= (battle.responders?.length ?? 0);
    return {
      ...state,
      phase: done ? 'battle' : 'muster',
      pendingBattle: { ...battle, participantIds, responseIndex },
      skipTurns: piece ? [...(state.skipTurns ?? []), faction] : state.skipTurns,
    };
  }
  if (state.phase !== 'play') throw new IllegalMoveError(`game is in phase '${state.phase}'`);

  // Only the turn clock passes (`timeoutMove`): the server never takes one from a player, since
  // `legalMoves` never lists it. It is no progress, so the quiet-turn draw keeps counting.
  if (move.type === 'pass') return endTurn(ctx, { ...state, turn: state.turn + 1 });
  // Nor is a draw ever taken from a player: the table plays it once every side has accepted one.
  if (move.type === 'draw')
    return { ...finish(state, state.pieces, state.factions, 'draw'), agreedDraw: true };

  if (move.type === 'spell') {
    if (!isLegalSpell(ctx, state, move))
      throw new IllegalMoveError(`spell ${move.spell} is not castable like that`);
    const after = {
      ...castSpell(ctx, state, move),
      turn: state.turn + 1,
      // Letting the round pass is not progress. It costs nothing and moves nothing, so counting it
      // as progress lets two players shift time at each other for ever with the quiet-turn draw
      // never firing -- which is exactly what two AIs did, for two thousand turns, once Shift Time
      // stopped being a once-a-game spell.
      lastProgressTurn: NO_PROGRESS_SPELLS.has(move.spell)
        ? state.lastProgressTurn
        : state.turn + 1,
    };
    // A summoned elemental attacks at once: the turn ends with its fight, like a capture.
    const caster = move.spell === 'shift-time' ? undefined : move.casterId;
    return after.phase === 'battle' ? after : endTurn(ctx, after, caster);
  }
  if (move.type !== 'move') throw new IllegalMoveError('muster response outside muster');

  const piece = pieceById(state, move.pieceId);
  if (!piece || piece.faction !== state.activeFaction) throw new IllegalMoveError('not your piece');
  const destination = destinationsFor(ctx, state, piece).find(
    (d) => d.x === move.x && d.y === move.y,
  );
  if (!destination)
    throw new IllegalMoveError(`piece ${piece.id} cannot reach ${move.x},${move.y}`);

  if (destination.attacks) {
    const participants = [piece.id, destination.attacks.id];
    const responders = optionsForMuster(ctx, state, participants, move.x, move.y);
    return {
      ...state,
      turn: state.turn + 1,
      phase: responders.length > 0 ? 'muster' : 'battle',
      pendingBattle: {
        attackerId: piece.id,
        defenderId: destination.attacks.id,
        x: move.x,
        y: move.y,
        participantIds: participants,
        responders,
        responseIndex: 0,
      },
    };
  }
  const pieces = state.pieces.map((p) => (p.id === piece.id ? { ...p, x: move.x, y: move.y } : p));
  return endTurn(ctx, { ...state, turn: state.turn + 1, pieces });
}

/** Settles the pending battle and finishes the attacker's turn. */
export function applyBattleResult(
  ctx: GameContext,
  state: GameState,
  result: BattleResult,
  rng = state.rng,
): GameState {
  const battle = state.pendingBattle;
  if (state.phase !== 'battle' || !battle) throw new IllegalMoveError('no battle pending');
  const participants = (battle.participantIds ?? [battle.attackerId, battle.defenderId])
    .map((id) => pieceById(state, id))
    .filter((piece): piece is Piece => !!piece);
  const dead = participants.filter((p) => p.id !== result.winnerId);
  // A summoned creature returns whence it came whatever the outcome (so a square it wins stays
  // empty) and never fills a graveyard: Revive brings back pieces, not spells.
  const summoned = (p: Piece) => ctx.roster[p.character]?.summoned === true;
  const gone = participants.filter((p) => dead.includes(p) || summoned(p));
  const factions = state.factions.map((f) => {
    const lost = dead.filter((p) => p.faction === f.id && !summoned(p)).map((p) => p.character);
    return lost.length > 0 ? { ...f, graveyard: [...f.graveyard, ...lost] } : f;
  });
  const pieces = state.pieces
    .filter((p) => !gone.includes(p))
    .map((p) =>
      p.id === result.winnerId
        ? { ...p, x: battle.x, y: battle.y, damage: result.winnerDamage }
        : p,
    );
  return endTurn(ctx, {
    ...state,
    phase: 'play',
    pendingBattle: null,
    pieces,
    factions,
    rng,
    lastProgressTurn: state.turn,
  });
}

/** Factions with a piece that can legally reach the contested cell, in board turn order. */
function optionsForMuster(
  ctx: GameContext,
  state: GameState,
  participants: PieceId[],
  x: number,
  y: number,
): FactionId[] {
  // The setup enables muster by placing this marker on the state. Older games stay immediate.
  if (state.engagement !== 'muster') return [];
  const committed = new Set(participants.map((id) => pieceById(state, id)?.faction));
  return state.factions
    .filter((f) => !f.eliminated && !committed.has(f.id))
    .filter((f) =>
      state.pieces.some(
        (piece) =>
          piece.faction === f.id &&
          destinationsFor(ctx, state, piece).some(
            (destination) => destination.x === x && destination.y === y,
          ),
      ),
    )
    .map((f) => f.id);
}

/**
 * Spells that are no progress towards the quiet-turn draw, no more than an ordinary move is.
 * Shifting time costs nothing and moves nothing at all; Teleport and Exchange put the same pieces
 * on different squares; Imprison, Release, Freeze and Curse hold a piece for a while and no longer.
 * What still counts is what lasts: a fight (Summon Elemental starts one), Heal and Revive.
 *
 * This is not bookkeeping. While every spell reset the count, the AI found that casting anything
 * at all kept a game going (`evaluate` then faded a lead as the quiet clock ran): whole games went
 * by with nineteen hundred spells cast and no fight ever taken. The holding spells did it more
 * quietly: in an Expert validation game a lone caster imprisoned the same Knight every 36 turns,
 * just inside the count, and the game ran some 880 turns past its last fight to the turn cap.
 */
const NO_PROGRESS_SPELLS: ReadonlySet<SpellId> = new Set<SpellId>([
  'shift-time',
  'teleport',
  'exchange',
  'imprison',
  'release',
  'freeze',
  'curse',
]);

/** Whether a faction holds every power point of the board. */
export function holdsAllPowerPoints(
  ctx: GameContext,
  state: GameState,
  faction: FactionId,
): boolean {
  const points = ctx.board.def.powerPoints;
  return (
    points.length > 0 &&
    points.every(([x, y]) =>
      state.pieces.some((p) => p.faction === faction && p.x === x && p.y === y),
    )
  );
}

/**
 * One of a side's turns has gone by, played or passed over: its pieces' holds wear down by one.
 * Counting after the turn rather than when it comes round is what makes a hold of five last five
 * of the victim's turns; counted on arrival, the fifth was already free. A neutral prisoner whose
 * sentence runs out walks free.
 */
function wearDown(pieces: Piece[], faction: FactionId): Piece[] {
  return pieces.map((p) => {
    if (p.faction !== faction || (p.frozen === 0 && p.cursed === 0 && !p.sentence)) return p;
    const worn = { ...p, frozen: Math.max(0, p.frozen - 1), cursed: Math.max(0, p.cursed - 1) };
    if (!p.sentence) return worn;
    const sentence = p.sentence - 1;
    return { ...worn, sentence, imprisoned: p.imprisoned && sentence > 0 };
  });
}

/**
 * Ends the active side's turn. `cast` is the piece that cast a spell this turn, if one did: it
 * gathers no magic from the square it stands on, having just spent some.
 */
function endTurn(ctx: GameContext, state: GameState, cast?: PieceId): GameState {
  // Pieces resting on a power point recover, in body and in magic. The ringed squares are what
  // make a second spell possible at all: a mage that spends its opening points has to go and stand
  // on one, in the open, on the squares the game is also won by holding.
  let pieces = state.pieces.map((p) => {
    if (p.faction !== state.activeFaction) return p;
    if (!cellAt(ctx.board, p.x, p.y)?.powerPoint) return p;
    const most = maxSpellPoints(ctx.roster[p.character]);
    // Card attrition and authored health can leave fractional wounds. Healing cannot
    // cross zero and turn wounds into a permanent bonus to health.
    const damage = Math.max(0, p.damage - 1);
    // Drawing magic out of a ringed square takes the turn: a caster that has just cast is still
    // spending, not gathering. Without this a mage that begins the game standing on one -- which
    // is where mages begin -- recharges as fast as the cheap spells cost, and never runs dry. A
    // game played that way came out the same whatever its seed, which is what gave it away.
    const gathering = !cast || p.id !== cast;
    const spellPoints = gathering ? Math.min(most, p.spellPoints + 1) : p.spellPoints;
    return damage === p.damage && spellPoints === p.spellPoints ? p : { ...p, damage, spellPoints };
  });

  const factions = state.factions.map((f) =>
    f.eliminated || pieces.some((p) => p.faction === f.id) ? f : { ...f, eliminated: true },
  );
  const alive = factions.filter((f) => !f.eliminated);

  if (holdsAllPowerPoints(ctx, state, state.activeFaction))
    return finish(state, pieces, factions, state.activeFaction);
  if (alive.length === 1) return finish(state, pieces, factions, alive[0]!.id);
  if (alive.length === 0) return finish(state, pieces, factions, 'draw');
  if (state.turn - state.lastProgressTurn >= MAX_QUIET_TURNS)
    return finish(state, pieces, factions, 'draw');
  if (state.turn >= MAX_GAME_TURNS) return finish(state, pieces, factions, 'draw');

  /**
   * Whether a side has anything it could do on its turn. Shift Time does not count: letting the
   * round pass is a choice, not an ability, and a side that can do nothing else would otherwise
   * skip for ever and hold the game open.
   */
  const canAct = (faction: FactionId, from: Piece[]) =>
    from.some((p) => p.faction === faction && !p.imprisoned && p.frozen === 0);

  // Light and Dark prisoners walk free when the cycle reaches their side's peak. Shift Time may
  // have brought it this very turn, and every round the walk below enters steps the cycle on; so
  // they are freed before a side is judged, and a side whose only pieces were held is not passed
  // over on the turn they walk out.
  const freeAtPeak = (from: Piece[], at: LuminosityState) =>
    from.map((p) =>
      p.imprisoned && isAtFavourablePeak(ctx, { ...state, luminosity: at }, p)
        ? { ...p, imprisoned: false }
        : p,
    );

  // The walk to the next side in order. The eliminated have no turns. A side that joined a muster
  // sits out its next turn, and a side whose every piece is frozen or imprisoned is passed over
  // rather than handed a turn it cannot take; either way its turn goes by and its holds count down,
  // which is what lets it thaw instead of locking the game. The walk goes on round after round
  // until a side can act, so it never stops on one that cannot: a side that sat a turn out is
  // reached on the next round. Holds always wear off in the end (the cycle comes back to every
  // peak), so the bound is only a guard; reaching it is a draw.
  pieces = freeAtPeak(wearDown(pieces, state.activeFaction), state.luminosity);
  const order = state.factions.map((f) => f.id);
  let index = order.indexOf(state.activeFaction);
  let round = state.round;
  const skipTurns = [...(state.skipTurns ?? [])];
  let luminosity = state.luminosity;
  let landed = false;
  for (let step = 0; step < order.length * MAX_WALK_ROUNDS && !landed; step++) {
    index = (index + 1) % order.length;
    if (index === 0) {
      round += 1;
      luminosity = advanceLuminosity(luminosity);
      pieces = freeAtPeak(pieces, luminosity);
    }
    const id = order[index]!;
    const skip = skipTurns.indexOf(id);
    if (skip >= 0) {
      skipTurns.splice(skip, 1);
      pieces = wearDown(pieces, id);
    } else if (!factions[index]!.eliminated && canAct(id, pieces)) landed = true;
    else pieces = wearDown(pieces, id);
  }
  const next: GameState = {
    ...state,
    pieces,
    factions,
    round,
    luminosity,
    activeFaction: order[index]!,
    skipTurns,
  };
  if (!landed) return finish(next, pieces, factions, 'draw');
  return next;
}

function finish(
  state: GameState,
  pieces: Piece[],
  factions: GameState['factions'],
  winner: FactionId | 'draw',
): GameState {
  return { ...state, pieces, factions, phase: 'ended', pendingBattle: null, winner };
}
